import { DualListBox, FormItem, FormLabel, Select } from '@/components/wm';
import { getStoreResource } from '@/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

export function SelectAlarms ({ data, onChangeField, onCompleted }) {
    const typeOptions = [
        { value: 'instantaneous', label: i18next.t('glossary.instantaneous').capitalize() },
        { value: 'average', label: i18next.t('glossary.average').capitalize() },
        { value: 'cumulative', label: i18next.t('glossary.cumulative').capitalize() }
    ];

    const formSchema = z.object({
        type: z.string().min(1, 'Type is required'),
        indicatorKind: z.string().min(1, 'Kind is required'),
        ids: z.array(z.any()).min(1, 'Please select at least one alarm')
    });

    const form = useForm({
        resolver: zodResolver(formSchema),
        defaultValues: {
            type: data.type || '',
            indicatorKind: data.indicatorKind || '',
            ids: data.ids || []
        },
        mode: 'onChange'
    });

    const selectedType = form.watch('type');
    const selectedKind = form.watch('indicatorKind');
    const selectedIds = form.watch('ids');

    const alarmOptions = useMemo(() => {
        if (!selectedKind || !selectedType) {
            return [];
        }

        const rawData  = getStoreResource('alarms', null, { type: selectedType }) || [];

        const data = rawData.reduce((acc, alarm) => {
            const indicator = getStoreResource('indicators', alarm.indicator_id);

            if (indicator && indicator.kind_id === selectedKind) {
                acc.push({
                    value: alarm.id,
                    label: `${alarm.name} (${indicator.name})`
                });
            }
            return acc;
        }, []);

        return data;
    }, [selectedType, selectedKind]);

    const indicatorKindOptions = useMemo(() => {
        const indicatorKinds  = getStoreResource('indicatorKinds') || [];

        const validOptions = indicatorKinds.filter(kind => {
            if (data.type === 'cumulative' && kind.cumulative === false) {
                return false;
            }

            const indicators = getStoreResource('indicators', null, { kind_id: kind.id }) || [];

            return indicators.some(indicator => {
                const alarms = getStoreResource('alarms', null, { indicator_id: indicator.id }) || [];
                return alarms.length > 0;
            });
        });

        return validOptions.map(item => ({
            label: i18next.t(`indicatorKinds.${item.slug}`).capitalize() || 'Unknown',
            value: String(item.id)
        }));
    }, [selectedType]);

    const errorMessage = useMemo(() => {
        if (selectedKind && selectedType && alarmOptions.length === 0) {
            return i18next.t('alarms.no_alarms_found').capitalize();
        }
        return form.formState.errors.ids?.message;
    }, [form.formState.errors.ids, selectedKind, selectedType, alarmOptions.length]);

    const handleFilterChange = (name, value) => {
        form.setValue(name, value, { shouldValidate: true });

        if (onChangeField) {
            onChangeField(name)(value);
        }
    };

    const handleIdsChange = newIds => {
        form.setValue('ids', newIds, { shouldValidate: true });

        if (onChangeField) {
            onChangeField('ids')(newIds);
        }
    };

    useEffect(() => {
        onCompleted(form.formState.isValid);
    }, [form.formState.isValid, onCompleted]);

    return (
        <div className="tw-w-full">
            <div className="tw-max-w-md">
                <FormItem>
                    <FormLabel>{i18next.t('common.type').capitalize()}</FormLabel>
                    <Select
                        className="!tw-mb-6"
                        disabled={selectedIds.length > 0}
                        onChange={value => handleFilterChange('type', value)}
                        options={typeOptions}
                        placeholder={i18next.t('ui.please_select_option').capitalize()}
                        value={selectedType}
                    />
                </FormItem>
                <FormItem>
                    <FormLabel>{i18next.t('glossary.indicator_kind').capitalize()}</FormLabel>
                    <Select
                        className="!tw-mb-6"
                        disabled={selectedIds.length > 0}
                        onChange={value => handleFilterChange('indicatorKind', value)}
                        options={indicatorKindOptions}
                        placeholder={i18next.t('ui.please_select_option').capitalize()}
                        value={selectedKind}
                    />
                </FormItem>
            </div>

            <DualListBox
                error={errorMessage}
                onChange={handleIdsChange}
                options={alarmOptions}
                searchPlaceholder={i18next.t('alarms.filter_alarms')}
                selectedIds={selectedIds}
                title={i18next.t('alarms.select_alarms')}
            />
        </div>
    );
}
